import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './AdminPage.module.css';
import api, { resolveMediaUrl } from '../api/api';
import { parseComicImageList, serializeComicContent } from '../utils/storyComicFormat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faShieldAlt, faSignOutAlt, faHome, faEye, faPlus, faEdit, faTrash, 
    faBullhorn, faHeart, faSave, faTimes, faUserShield,
    faUserPlus, faCheck, faBan, faPhotoVideo, faImage
} from '@fortawesome/free-solid-svg-icons';

const STORY_CATEGORIES = [
    { value: 'comic', label: '홍보툰' },
    { value: 'youtube', label: '유튜브' },
    { value: 'instagram', label: '인스타' },
    { value: 'donor', label: '기부자' },
    { value: 'column', label: '칼럼' },
    { value: 'press', label: '언론 보도' },
];

// Helper functions
const formatCurrency = (num) => `${new Intl.NumberFormat('ko-KR').format(num)}원`;
const formatDate = (dateStr) => new Date(dateStr).toLocaleString('ko-KR');

// Alert Component
const Alert = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className={`${styles.alert} ${styles[`alert${type.charAt(0).toUpperCase() + type.slice(1)}`]}`}>
            {message}
        </div>
    );
};

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
        <div className={styles.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2>{title}</h2>
                    <span className={styles.close} onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </span>
                </div>
                {children}
            </div>
        </div>
    );
};

// Campaign Form Component
const CampaignForm = ({ campaign, onSubmit, onClose, token }) => {
    const [formData, setFormData] = useState({
        title: campaign?.title || '',
        hospital: campaign?.hospital || '',
        description: campaign?.description || '',
        target_amount: campaign?.target_amount || '',
        current_amount: campaign?.current_amount || 0,
        category: campaign?.category || 'medical_equipment'
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // 입력 검증
            if (!formData.title.trim()) {
                throw new Error('제목을 입력해주세요.');
            }
            if (!formData.hospital.trim()) {
                throw new Error('병원명을 입력해주세요.');
            }
            if (!formData.description.trim()) {
                throw new Error('설명을 입력해주세요.');
            }
            if (!formData.target_amount || formData.target_amount <= 0) {
                throw new Error('목표 금액을 올바르게 입력해주세요.');
            }

            const data = {
                title: formData.title.trim(),
                hospital: formData.hospital.trim(),
                description: formData.description.trim(),
                target_amount: parseInt(formData.target_amount),
                category: formData.category.trim()
            };

            // 수정 모드에서만 current_amount 포함
            if (campaign?.id) {
                data.current_amount = parseInt(formData.current_amount);
            }

            console.log('Submitting campaign data:', data); // 디버깅용

            if (campaign?.id) {
                // 수정 모드
                await api.updateCampaign(campaign.id, data, token);
            } else {
                // 생성 모드
                await api.createCampaignAsAdmin(data, token);
            }
            onSubmit();
        } catch (error) {
            console.error('Campaign form error:', error);
            // 오류 메시지 개선
            let errorMessage = '알 수 없는 오류가 발생했습니다.';
            if (error.message) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            } else if (error.detail) {
                errorMessage = error.detail;
            }
            alert(`오류가 발생했습니다: ${errorMessage}`);
        }
        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
                <label htmlFor="title">제목</label>
                <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className={styles.formGroup}>
                <label htmlFor="hospital">병원</label>
                <input
                    type="text"
                    id="hospital"
                    name="hospital"
                    value={formData.hospital}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className={styles.formGroup}>
                <label htmlFor="description">설명</label>
                <textarea
                    id="description"
                    name="description"
                    rows="4"
                    value={formData.description}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className={styles.formGroup}>
                <label htmlFor="target_amount">목표 금액</label>
                <input
                    type="number"
                    id="target_amount"
                    name="target_amount"
                    value={formData.target_amount}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className={styles.formGroup}>
                <label htmlFor="current_amount">현재 금액</label>
                <input
                    type="number"
                    id="current_amount"
                    name="current_amount"
                    value={formData.current_amount}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className={styles.formGroup}>
                <label htmlFor="category">카테고리</label>
                <input
                    type="text"
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                />
            </div>
            <button 
                type="submit" 
                className={`${styles.btn} ${styles.btnPrimary}`} 
                style={{ width: '100%' }}
                disabled={loading}
            >
                <FontAwesomeIcon icon={faSave} /> {loading ? '저장 중...' : '저장하기'}
            </button>
        </form>
    );
};

// Donation Form Component
const DonationForm = ({ donation, campaigns, onSubmit, onClose, token, isEdit = false }) => {
    const [formData, setFormData] = useState({
        campaign_id: donation?.campaign_id || '',
        donor_name: donation?.donor_name || '',
        donor_email: donation?.donor_email || '',
        amount: donation?.amount || '',
        message: donation?.message || ''
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = {
                ...formData,
                amount: parseInt(formData.amount)
            };

            if (isEdit && donation?.id) {
                await api.updateDonation(donation.id, data, token);
            } else {
                data.campaign_id = parseInt(formData.campaign_id);
                await api.createDonationByAdmin(data, token);
            }
            onSubmit();
        } catch (error) {
            console.error('Donation form error:', error);
        }
        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            {!isEdit && (
                <div className={styles.formGroup}>
                    <label htmlFor="campaign_id">캠페인 선택</label>
                    <select
                        id="campaign_id"
                        name="campaign_id"
                        value={formData.campaign_id}
                        onChange={handleChange}
                        required
                    >
                        <option value="">캠페인을 선택하세요</option>
                        {campaigns.map(campaign => (
                            <option key={campaign.id} value={campaign.id}>
                                #{campaign.id}: {campaign.title} ({campaign.hospital})
                            </option>
                        ))}
                    </select>
                </div>
            )}
            <div className={styles.formGroup}>
                <label htmlFor="donor_name">기부자명</label>
                <input
                    type="text"
                    id="donor_name"
                    name="donor_name"
                    value={formData.donor_name}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className={styles.formGroup}>
                <label htmlFor="donor_email">이메일</label>
                <input
                    type="email"
                    id="donor_email"
                    name="donor_email"
                    value={formData.donor_email}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className={styles.formGroup}>
                <label htmlFor="amount">기부 금액</label>
                <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className={styles.formGroup}>
                <label htmlFor="message">메시지</label>
                <textarea
                    id="message"
                    name="message"
                    rows="3"
                    value={formData.message}
                    onChange={handleChange}
                />
            </div>
            <button 
                type="submit" 
                className={`${styles.btn} ${styles.btnPrimary}`} 
                style={{ width: '100%' }}
                disabled={loading}
            >
                <FontAwesomeIcon icon={faSave} /> {loading ? '저장 중...' : '저장하기'}
            </button>
        </form>
    );
};

// Story Form — 업로드 → publicUrl → serializeComicContent → api.createStory
const StoryForm = ({ story, onSubmit, onClose, token }) => {
    const newMediaFolderId = () =>
        (typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `tmp_${Date.now()}`);

    const [formData, setFormData] = useState({
        title: story?.title || '',
        category: story?.category || 'comic',
        image: story?.image || '',
        summary: story?.summary || '',
        content: story?.content || '',
        display_order: story?.display_order ?? 0,
        is_published: story?.is_published !== false,
    });
    const [mediaFolderId, setMediaFolderId] = useState(
        story?.id ? String(story.id) : newMediaFolderId()
    );
    const [comicPages, setComicPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingPages, setUploadingPages] = useState(false);

    useEffect(() => {
        setFormData({
            title: story?.title || '',
            category: story?.category || 'comic',
            image: story?.image || '',
            summary: story?.summary || '',
            content: story?.content || '',
            display_order: story?.display_order ?? 0,
            is_published: story?.is_published !== false,
        });
        setMediaFolderId(story?.id ? String(story.id) : newMediaFolderId());
        setComicPages(story?.category === 'comic' ? parseComicImageList(story.content) : []);
    }, [story]);

    const storageStoryId = story?.id ? String(story.id) : mediaFolderId;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const res = await api.uploadStoryMedia(
                file,
                { category: formData.category, storyId: storageStoryId, kind: 'thumbnail' },
                token
            );
            const url = res.publicUrl || res.url;
            if (url) setFormData((prev) => ({ ...prev, image: url }));
        } catch (err) {
            alert(err.message || '이미지 업로드에 실패했습니다.');
        }
        setUploading(false);
        e.target.value = '';
    };

    const handleComicPagesFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploadingPages(true);
        try {
            const next = [...comicPages];
            for (let i = 0; i < files.length; i++) {
                const res = await api.uploadStoryMedia(
                    files[i],
                    {
                        category: 'comic',
                        storyId: storageStoryId,
                        kind: 'page',
                        pageIndex: next.length + i,
                    },
                    token
                );
                const url = res.publicUrl || res.url;
                if (url) next.push(url);
            }
            setComicPages(next);
        } catch (err) {
            alert(err.message || '페이지 이미지 업로드에 실패했습니다.');
        }
        setUploadingPages(false);
        e.target.value = '';
    };

    const removeComicPage = (index) => {
        setComicPages((prev) => prev.filter((_, i) => i !== index));
    };

    const moveComicPage = (index, dir) => {
        setComicPages((prev) => {
            const j = index + dir;
            if (j < 0 || j >= prev.length) return prev;
            const copy = [...prev];
            [copy[index], copy[j]] = [copy[j], copy[index]];
            return copy;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (!formData.title.trim()) throw new Error('제목을 입력해주세요.');
            if (!formData.summary.trim()) throw new Error('소개 문구를 입력해주세요.');

            let payloadContent;
            let payloadImage = formData.image.trim() || null;

            if (formData.category === 'comic') {
                if (!comicPages.length) {
                    throw new Error('웹툰 페이지 이미지를 1장 이상 추가해주세요.');
                }
                payloadContent = serializeComicContent(comicPages);
                if (!payloadImage) payloadImage = comicPages[0];
            } else if (formData.category === 'youtube' || formData.category === 'instagram') {
                if (!formData.content.trim()) {
                    throw new Error(
                        formData.category === 'youtube'
                            ? '유튜브 URL을 입력해주세요.'
                            : '인스타그램 URL을 입력해주세요.'
                    );
                }
                payloadContent = formData.content.trim();
            } else {
                if (!formData.content.trim()) throw new Error('본문을 입력해주세요.');
                payloadContent = formData.content.trim();
            }

            const payload = {
                title: formData.title.trim(),
                category: formData.category,
                image: payloadImage,
                summary: formData.summary.trim(),
                content: payloadContent,
                display_order: Number(formData.display_order) || 0,
                is_published: Boolean(formData.is_published),
            };

            if (story?.id) {
                await api.updateStory(story.id, payload, token);
            } else {
                await api.createStory(payload, token);
            }
            onSubmit();
        } catch (error) {
            console.error('Story form error:', error);
            alert(error.message || '저장에 실패했습니다.');
        }
        setLoading(false);
    };

    const isComic = formData.category === 'comic';
    const isYoutube = formData.category === 'youtube';
    const isInstagram = formData.category === 'instagram';

    const contentHint = isYoutube
        ? '유튜브 URL (watch, youtu.be). 썸네일은 선택입니다.'
        : isInstagram
          ? '인스타 permalink (instagram.com/p/…, /reel/…). 썸네일은 선택입니다.'
          : '본문 텍스트를 입력하세요.';

    const contentRequired = isYoutube || isInstagram || (!isComic && !isYoutube && !isInstagram);

    return (
        <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
                <label htmlFor="story_title">제목</label>
                <input
                    id="story_title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className={styles.formGroup}>
                <label htmlFor="story_category">카테고리</label>
                <select id="story_category" name="category" value={formData.category} onChange={handleChange}>
                    {STORY_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                            {c.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="story_display_order">표시 순서 (큰 숫자가 위)</label>
                <input
                    id="story_display_order"
                    name="display_order"
                    type="number"
                    min={0}
                    value={formData.display_order}
                    onChange={handleChange}
                />
            </div>

            <div className={styles.formGroup}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        name="is_published"
                        checked={formData.is_published}
                        onChange={handleChange}
                    />
                    공개 (Stories 페이지에 표시)
                </label>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="story_image">
                    {isComic ? '목록 썸네일 (비우면 첫 페이지와 동일)' : '썸네일 (선택)'}
                </label>
                <input
                    id="story_image"
                    name="image"
                    type="text"
                    placeholder={
                        isComic
                            ? '비우면 첫 웹툰 페이지가 썸네일로 사용됩니다'
                            : 'Supabase URL 또는 파일 업로드'
                    }
                    value={formData.image}
                    onChange={handleChange}
                />
                <div style={{ marginTop: 8 }}>
                    <label className={`${styles.btn} ${styles.btnSecondary}`} style={{ cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faImage} />
                        {uploading ? '업로드 중...' : '썸네일 파일 업로드'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleImageUpload} disabled={uploading} />
                    </label>
                </div>
                {formData.image && (
                    <div style={{ marginTop: 8 }}>
                        <img src={resolveMediaUrl(formData.image)} alt="" style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 4 }} />
                    </div>
                )}
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="story_summary">{isComic ? '카드 소개 (목록에 표시되는 한 줄)' : '요약'}</label>
                <textarea id="story_summary" name="summary" rows={2} value={formData.summary} onChange={handleChange} required />
            </div>

            {isComic && (
                <div className={styles.formGroup}>
                    <label>웹툰 페이지 이미지 (위에서부터 1페이지 — StoriesPage 뷰어와 동일)</label>
                    <p style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b' }}>
                        여러 장을 한 번에 선택해 순서대로 추가할 수 있습니다. 순서는 위/아래로 바꿀 수 있습니다.
                    </p>
                    <label className={`${styles.btn} ${styles.btnSecondary}`} style={{ cursor: 'pointer', marginBottom: 12 }}>
                        <FontAwesomeIcon icon={faPlus} />
                        {uploadingPages ? '업로드 중...' : '페이지 이미지 추가 (다중 선택 가능)'}
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            hidden
                            onChange={handleComicPagesFiles}
                            disabled={uploadingPages}
                        />
                    </label>
                    <ol style={{ margin: 0, paddingLeft: 20, listStyle: 'decimal' }}>
                        {comicPages.map((path, idx) => (
                            <li key={`${path}-${idx}`} style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ minWidth: 28 }}>{idx + 1}</span>
                                <img
                                    src={resolveMediaUrl(path)}
                                    alt=""
                                    style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, border: '1px solid #e2e8f0' }}
                                />
                                <span style={{ fontSize: 12, color: '#64748b', flex: 1, minWidth: 120, wordBreak: 'break-all' }}>{path}</span>
                                <button type="button" className={styles.editBtn} title="위로" onClick={() => moveComicPage(idx, -1)} disabled={idx === 0}>
                                    ↑
                                </button>
                                <button type="button" className={styles.editBtn} title="아래로" onClick={() => moveComicPage(idx, 1)} disabled={idx === comicPages.length - 1}>
                                    ↓
                                </button>
                                <button type="button" className={styles.deleteBtn} title="삭제" onClick={() => removeComicPage(idx)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </li>
                        ))}
                    </ol>
                    {!comicPages.length && (
                        <p style={{ color: '#94a3b8', fontSize: 14 }}>페이지 이미지가 없습니다. 홍보툰 카드·뷰어에 표시할 이미지를 추가하세요.</p>
                    )}
                </div>
            )}

            {!isComic && (
                <div className={styles.formGroup}>
                    <label htmlFor="story_content">
                        {isYoutube ? '유튜브 링크' : isInstagram ? '인스타 링크' : '본문'}
                    </label>
                    <textarea
                        id="story_content"
                        name="content"
                        rows={8}
                        value={formData.content}
                        onChange={handleChange}
                        required={contentRequired}
                    />
                    <small style={{ color: '#64748b', display: 'block', marginTop: 4 }}>{contentHint}</small>
                </div>
            )}

            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} style={{ width: '100%' }} disabled={loading || uploading || uploadingPages}>
                <FontAwesomeIcon icon={faSave} /> {loading ? '저장 중...' : '저장하기'}
            </button>
        </form>
    );
};

const StoriesTable = ({ stories, onEdit, onDelete }) => {
    const categoryLabel = (value) => STORY_CATEGORIES.find((c) => c.value === value)?.label || value;

    if (!stories.length) {
        return (
            <p style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                등록된 스토리가 없습니다. &quot;새 스토리&quot;로 추가하세요.
            </p>
        );
    }

    return (
        <div className={styles.dataTableContainer}>
            <table className={styles.dataTable}>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>썸네일</th>
                        <th>제목</th>
                        <th>카테고리</th>
                        <th>요약</th>
                        <th>등록일</th>
                        <th>작업</th>
                    </tr>
                </thead>
                <tbody>
                    {stories.map((s) => (
                        <tr key={s.id}>
                            <td>{s.id}</td>
                            <td>
                                {s.image ? (
                                    <img
                                        src={resolveMediaUrl(s.image)}
                                        alt=""
                                        style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }}
                                    />
                                ) : (
                                    '—'
                                )}
                            </td>
                            <td style={{ maxWidth: 200 }}>{s.title}</td>
                            <td>{categoryLabel(s.category)}</td>
                            <td style={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.summary}</td>
                            <td>{s.created_at ? formatDate(s.created_at) : '—'}</td>
                            <td>
                                <div className={styles.actionBtns}>
                                    <button type="button" className={styles.editBtn} onClick={() => onEdit(s)}>
                                        <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <button type="button" className={styles.deleteBtn} onClick={() => onDelete(s.id)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// LoginPage Component
const LoginPage = ({ onLogin }) => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const data = await api.adminLogin(password);
            onLogin(data.access_token);
        } catch (err) {
            setError(err.message || '로그인에 실패했습니다.');
        }
        setLoading(false);
    };

    return (
        <div className={styles.loginForm}>
            <h2><FontAwesomeIcon icon={faShieldAlt} /> 관리자 로그인</h2>
            {error && <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>}
            <form onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                    <label htmlFor="password">비밀번호</label>
                    <input 
                        type="password" 
                        id="password" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        required 
                    />
                </div>
                <button 
                    type="submit" 
                    className={`${styles.btn} ${styles.btnPrimary}`} 
                    style={{ width: '100%' }}
                    disabled={loading}
                >
                    <FontAwesomeIcon icon={faShieldAlt} /> {loading ? '로그인 중...' : '로그인'}
                </button>
            </form>
        </div>
    );
};

// StatCard Component
const StatCard = ({ number, label }) => (
    <div className={styles.statCard}>
        <div className={styles.statNumber}>{number}</div>
        <div className={styles.statLabel}>{label}</div>
    </div>
);

// CampaignsTable Component
const CampaignsTable = ({ campaigns, onEdit, onDelete }) => (
    <div className={styles.dataTableContainer}>
        <table className={styles.dataTable}>
            <thead>
                <tr>
                    <th>ID</th>
                    <th>제목</th>
                    <th>병원</th>
                    <th>모금 현황</th>
                    <th>생성일</th>
                    <th>수정</th>
                </tr>
            </thead>
            <tbody>
                {campaigns.map(c => (
                    <tr key={c.id}>
                        <td>{c.id}</td>
                        <td>{c.title}</td>
                        <td>{c.hospital}</td>
                        <td>
                            <span className={styles.amount}>{formatCurrency(c.current_amount)}</span> / {formatCurrency(c.target_amount)}
                            <div className={styles.progressInfo}>({((c.current_amount / c.target_amount) * 100).toFixed(1)}%)</div>
                        </td>
                        <td>{formatDate(c.created_at)}</td>
                        <td>
                            <div className={styles.actionBtns}>
                                <button className={styles.editBtn} onClick={() => onEdit(c)}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button className={styles.deleteBtn} onClick={() => onDelete(c.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// VolunteerApplicationsTable Component
const VolunteerApplicationsTable = ({ applications, onStatusChange }) => {
    const getCategoryLabel = (category) => {
        const labels = {
            developer: '개발 도우미',
            medical: '의료 전문가',
            admin: '운영 관리자',
            sponsor: '후원처 추천'
        };
        return labels[category] || category;
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: { label: '대기중', className: styles.badgePending },
            approved: { label: '승인됨', className: styles.badgeApproved },
            rejected: { label: '거절됨', className: styles.badgeRejected }
        };
        const badge = badges[status] || { label: status, className: '' };
        return <span className={`${styles.badge} ${badge.className}`}>{badge.label}</span>;
    };

    return (
        <div className={styles.dataTableContainer}>
            <table className={styles.dataTable}>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>이름</th>
                        <th>분야</th>
                        <th>연락처</th>
                        <th>이메일</th>
                        <th>신청일</th>
                        <th>상태</th>
                        <th>작업</th>
                    </tr>
                </thead>
                <tbody>
                    {applications.map(app => (
                        <tr key={app.id}>
                            <td>{app.id}</td>
                            <td>{app.name}</td>
                            <td>{getCategoryLabel(app.category)}</td>
                            <td>{app.phone}</td>
                            <td>{app.email}</td>
                            <td>{new Date(app.applied_at).toLocaleDateString('ko-KR')}</td>
                            <td>{getStatusBadge(app.status)}</td>
                            <td>
                                <div className={styles.actionBtns}>
                                    {app.status === 'pending' && (
                                        <>
                                            <button 
                                                className={styles.approveBtn} 
                                                onClick={() => onStatusChange(app.id, 'approved')}
                                                title="승인"
                                            >
                                                <FontAwesomeIcon icon={faCheck} />
                                            </button>
                                            <button 
                                                className={styles.rejectBtn} 
                                                onClick={() => onStatusChange(app.id, 'rejected')}
                                                title="거절"
                                            >
                                                <FontAwesomeIcon icon={faBan} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// MembersTable Component
const MembersTable = ({ members }) => {
    const getRoleBadge = (role) => {
        const badges = {
            developer: { label: '개발팀', className: styles.badgeDeveloper },
            medical: { label: '의료자문', className: styles.badgeMedical },
            admin: { label: '운영팀', className: styles.badgeAdmin },
            founder: { label: '설립자', className: styles.badgeFounder }
        };
        const badge = badges[role] || { label: role, className: '' };
        return <span className={`${styles.badge} ${badge.className}`}>{badge.label}</span>;
    };

    return (
        <div className={styles.dataTableContainer}>
            <table className={styles.dataTable}>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>이름</th>
                        <th>역할</th>
                        <th>이메일</th>
                        <th>연락처</th>
                        <th>가입일</th>
                        <th>상태</th>
                    </tr>
                </thead>
                <tbody>
                    {members.map(member => (
                        <tr key={member.id}>
                            <td>{member.id}</td>
                            <td>{member.name}</td>
                            <td>{getRoleBadge(member.role)}</td>
                            <td>{member.email}</td>
                            <td>{member.phone}</td>
                            <td>{new Date(member.joined_at).toLocaleDateString('ko-KR')}</td>
                            <td>
                                <span className={`${styles.badge} ${styles.badgeActive}`}>활동중</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// DonationsTable Component
const DonationsTable = ({ donations, onEdit, onDelete }) => (
    <div className={styles.dataTableContainer}>
        <table className={styles.dataTable}>
            <thead>
                <tr>
                    <th>ID</th>
                    <th>캠페인</th>
                    <th>기부자</th>
                    <th>이메일</th>
                    <th>금액</th>
                    <th>기부일</th>
                    <th>수정</th>
                </tr>
            </thead>
            <tbody>
                {donations.map(d => (
                    <tr key={d.id}>
                        <td>{d.id}</td>
                        <td>#{d.campaign_id}: {d.campaign_title}</td>
                        <td>{d.donor_name}</td>
                        <td>{d.donor_email}</td>
                        <td className={styles.amount}>{formatCurrency(d.amount)}</td>
                        <td>{formatDate(d.donated_at)}</td>
                        <td>
                            <div className={styles.actionBtns}>
                                <button className={styles.editBtn} onClick={() => onEdit(d)}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button className={styles.deleteBtn} onClick={() => onDelete(d.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// Dashboard Component
const Dashboard = ({ token, onLogout }) => {
    const [stats, setStats] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [donations, setDonations] = useState([]);
    const [applications, setApplications] = useState([]);
    const [stories, setStories] = useState([]);

    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);

    // Modal states
    const [campaignModal, setCampaignModal] = useState({ isOpen: false, campaign: null });
    const [donationModal, setDonationModal] = useState({ isOpen: false, donation: null, isEdit: false });
    const [storyModal, setStoryModal] = useState({ isOpen: false, story: null });
    const [activeTab, setActiveTab] = useState('campaigns'); // campaigns | donations | volunteers | stories

    const showAlert = (message, type = 'success') => {
        // 메시지가 객체인 경우 처리
        let displayMessage = message;
        if (typeof message === 'object') {
            if (message.message) {
                displayMessage = message.message;
            } else if (message.detail) {
                displayMessage = message.detail;
            } else {
                displayMessage = JSON.stringify(message);
            }
        }
        
        setAlert({ message: displayMessage, type });
        setTimeout(() => setAlert(null), 5000); // 5초로 연장
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsData, transparencyData, applicationsData, storiesData] = await Promise.all([
                api.getDashboardStats(token),
                api.getAllTransparencyData(),
                api.getVolunteerApplications(token).catch(() => []),
                api.getStories(null, 1, 200).catch(() => ({ stories: [] })),
            ]);
            setStats(statsData.stats);
            setCampaigns(transparencyData.campaigns);
            setDonations(transparencyData.donations);
            setApplications(applicationsData);
            setStories(storiesData.stories || []);
        } catch (error) {
            console.error("Failed to fetch dashboard data:", error);
            showAlert('데이터를 불러오는데 실패했습니다.', 'error');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [token]);

    // Campaign handlers
    const handleCreateCampaign = () => {
        setCampaignModal({ isOpen: true, campaign: null });
    };

    const handleEditCampaign = (campaign) => {
        setCampaignModal({ isOpen: true, campaign });
    };

    const handleDeleteCampaign = async (id) => {
        if (!window.confirm(`정말로 캠페인 #${id}를 삭제하시겠습니까? 관련 기부 내역도 모두 삭제됩니다.`)) return;
        try {
            await api.deleteCampaign(id, token);
            showAlert('캠페인이 삭제되었습니다.');
            fetchData();
        } catch (error) {
            showAlert('캠페인 삭제에 실패했습니다.', 'error');
        }
    };

    const handleCampaignSubmit = () => {
        setCampaignModal({ isOpen: false, campaign: null });
        showAlert('캠페인이 성공적으로 저장되었습니다.');
        fetchData();
    };

    // Donation handlers
    const handleCreateDonation = () => {
        setDonationModal({ isOpen: true, donation: null, isEdit: false });
    };

    const handleEditDonation = (donation) => {
        setDonationModal({ isOpen: true, donation, isEdit: true });
    };

    const handleDeleteDonation = async (id) => {
        if (!window.confirm(`정말로 기부 #${id} 내역을 삭제하시겠습니까?`)) return;
        try {
            await api.deleteDonation(id, token);
            showAlert('기부 내역이 삭제되었습니다.');
            fetchData();
        } catch (error) {
            showAlert('기부 내역 삭제에 실패했습니다.', 'error');
        }
    };

    const handleDonationSubmit = () => {
        setDonationModal({ isOpen: false, donation: null, isEdit: false });
        showAlert('기부 내역이 성공적으로 저장되었습니다.');
        fetchData();
    };

    // Volunteer Application handlers
    const handleApplicationStatusChange = async (applicationId, newStatus) => {
        try {
            await api.updateVolunteerApplicationStatus(applicationId, newStatus, token);
            showAlert(`신청 상태가 ${newStatus === 'approved' ? '승인' : '거절'}되었습니다.`);
            fetchData();
        } catch (error) {
            console.error("Failed to update application status:", error);
            showAlert('신청 상태 변경에 실패했습니다.', 'error');
        }
    };

    const handleCreateStory = () => setStoryModal({ isOpen: true, story: null });
    const handleEditStory = (story) => setStoryModal({ isOpen: true, story });

    const handleDeleteStory = async (id) => {
        if (!window.confirm(`스토리 #${id}를 삭제할까요?`)) return;
        try {
            await api.deleteStory(id, token);
            showAlert('스토리가 삭제되었습니다.');
            fetchData();
        } catch (error) {
            showAlert('스토리 삭제에 실패했습니다.', 'error');
        }
    };

    const handleStorySubmit = () => {
        setStoryModal({ isOpen: false, story: null });
        showAlert('스토리가 저장되었습니다.');
        fetchData();
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>데이터를 불러오는 중...</div>
            </div>
        );
    }

    return (
        <div className={styles.dashboard}>
            <div className={styles.header}>
                <div className={styles.logo}>
                    <FontAwesomeIcon icon={faUserShield} /> TraumaHelp 관리자
                </div>
                <div className={styles.navButtons}>
                    <Link to="/" className={`${styles.btn} ${styles.btnSecondary}`} target="_blank">
                        <FontAwesomeIcon icon={faHome} /> 메인 사이트
                    </Link>
                    <a href="/transparency" className={`${styles.btn} ${styles.btnSecondary}`} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faEye} /> 투명성 대시보드
                    </a>
                    <button onClick={onLogout} className={`${styles.btn} ${styles.btnPrimary}`}>
                        <FontAwesomeIcon icon={faSignOutAlt} /> 로그아웃
                    </button>
                </div>
            </div>

            {alert && (
                <Alert 
                    message={alert.message} 
                    type={alert.type} 
                    onClose={() => setAlert(null)} 
                />
            )}

            {stats && (
                <div className={styles.statsGrid}>
                    <StatCard number={stats.total_campaigns} label="총 캠페인" />
                    <StatCard number={stats.total_donations ?? donations.length} label="총 기부 건수" />
                    <StatCard number={stats.unique_donors ?? stats.total_donors} label="고유 기부자" />
                    <StatCard number={formatCurrency(stats.total_amount)} label="총 기부 금액" />
                    <StatCard number={stats.total_hospitals ?? stats.supported_hospitals} label="지원 병원" />
                </div>
            )}

            <div className={styles.dataSection}>
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'campaigns' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('campaigns')}
                    >
                        <FontAwesomeIcon icon={faBullhorn} /> 캠페인 관리
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'donations' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('donations')}
                    >
                        <FontAwesomeIcon icon={faHeart} /> 기부 내역 관리
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'volunteers' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('volunteers')}
                    >
                        <FontAwesomeIcon icon={faUserPlus} /> 봉사자 신청 관리
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'stories' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('stories')}
                    >
                        <FontAwesomeIcon icon={faPhotoVideo} /> 스토리 · 영상
                    </button>
                </div>

                {activeTab === 'campaigns' && (
                    <>
                        <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>
                                <FontAwesomeIcon icon={faBullhorn} /> 캠페인 관리
                            </h2>
                            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleCreateCampaign}>
                                <FontAwesomeIcon icon={faPlus} /> 새 캠페인
                            </button>
                        </div>
                        <CampaignsTable 
                            campaigns={campaigns} 
                            onEdit={handleEditCampaign}
                            onDelete={handleDeleteCampaign}
                        />
                    </>
                )}

                {activeTab === 'donations' && (
                    <>
                        <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>
                                <FontAwesomeIcon icon={faHeart} /> 기부 내역 관리
                            </h2>
                            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleCreateDonation}>
                                <FontAwesomeIcon icon={faPlus} /> 새 기부 내역
                            </button>
                        </div>
                        <DonationsTable 
                            donations={donations} 
                            onEdit={handleEditDonation}
                            onDelete={handleDeleteDonation}
                        />
                    </>
                )}

                {activeTab === 'volunteers' && (
                    <>
                        <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>
                                <FontAwesomeIcon icon={faUserPlus} /> 봉사자 신청 관리
                            </h2>
                            <span className={styles.sectionInfo}>
                                신청 {applications.length}건 | 대기중 {applications.filter(a => a.status === 'pending').length}건
                            </span>
                        </div>
                        <VolunteerApplicationsTable 
                            applications={applications}
                            onStatusChange={handleApplicationStatusChange}
                        />
                    </>
                )}

                {activeTab === 'stories' && (
                    <>
                        <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>
                                <FontAwesomeIcon icon={faPhotoVideo} /> 스토리 · 유튜브
                            </h2>
                            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleCreateStory}>
                                <FontAwesomeIcon icon={faPlus} /> 새 스토리
                            </button>
                        </div>
                        <p className={styles.sectionInfo} style={{ marginBottom: 16 }}>
                            웹툰: 이미지 업로드 → <code>serializeComicContent</code> JSON 저장.
                            유튜브/인스타: URL + 선택 썸네일.
                            API: <code>/api/admin/stories</code>, <code>/api/admin/stories/media</code>
                            (미구현 시 <code>/api/upload/image</code> 폴백)
                        </p>
                        <StoriesTable stories={stories} onEdit={handleEditStory} onDelete={handleDeleteStory} />
                    </>
                )}
            </div>

            {/* Campaign Modal */}
            <Modal
                isOpen={campaignModal.isOpen}
                onClose={() => setCampaignModal({ isOpen: false, campaign: null })}
                title={campaignModal.campaign ? '캠페인 수정' : '새 캠페인'}
            >
                <CampaignForm
                    campaign={campaignModal.campaign}
                    onSubmit={handleCampaignSubmit}
                    onClose={() => setCampaignModal({ isOpen: false, campaign: null })}
                    token={token}
                />
            </Modal>

            {/* Donation Modal */}
            <Modal
                isOpen={donationModal.isOpen}
                onClose={() => setDonationModal({ isOpen: false, donation: null, isEdit: false })}
                title={donationModal.isEdit ? `기부 내역 수정 (#${donationModal.donation?.id})` : '새 기부 내역'}
            >
                <DonationForm
                    donation={donationModal.donation}
                    campaigns={campaigns}
                    onSubmit={handleDonationSubmit}
                    onClose={() => setDonationModal({ isOpen: false, donation: null, isEdit: false })}
                    token={token}
                    isEdit={donationModal.isEdit}
                />
            </Modal>

            <Modal
                isOpen={storyModal.isOpen}
                onClose={() => setStoryModal({ isOpen: false, story: null })}
                title={storyModal.story ? `스토리 수정 (#${storyModal.story.id})` : '새 스토리'}
            >
                <StoryForm
                    story={storyModal.story}
                    onSubmit={handleStorySubmit}
                    onClose={() => setStoryModal({ isOpen: false, story: null })}
                    token={token}
                />
            </Modal>
        </div>
    );
};

// AdminPage Component
const AdminPage = () => {
    const [token, setToken] = useState(localStorage.getItem('adminToken'));

    const handleLogin = (newToken) => {
        localStorage.setItem('adminToken', newToken);
        setToken(newToken);
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        setToken(null);
    };

    return (
        <div className={styles.container}>
            {token ? (
                <Dashboard token={token} onLogout={handleLogout} />
            ) : (
                <LoginPage onLogin={handleLogin} />
            )}
        </div>
    );
};

export default AdminPage;